import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { format } from 'date-fns';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import * as m from '../../../../localization/out/messages.js';
import { subscribeToJobPositionsByCompany } from '../../../api/job-position.js';
import { JobPosition } from '../../../db/job-position.js';
import { formatFormError } from '../../components/format-form-error.js';
import { translateEnum } from '../../translate-enum.js';
import { companyRouter } from './company-routes.js';

const JobCard = stateful(
  (props: { job: JobPosition; onDelete: (job: JobPosition) => void; onDuplicate: (job: JobPosition) => void }) => {
    const handleDetails = () => {
      goTo(companyRouter.link('jobDetail') + '/' + props.job._id!);
    };

    return html`
      <div class="card bg-base-100 shadow cursor-pointer" @click=${() => handleDetails()}>
        <div class="card-body">
          <h2 class="card-title">${props.job.title}</h2>
          <p>
            <strong>${m.company_jobs_card_type_label()}</strong> ${translateEnum(
              'job_form_enum',
              props.job.employmentType ?? undefined,
            )}
          </p>
          <p><strong>${m.company_jobs_card_location_label()}</strong> ${props.job.location}</p>
          <p><strong>${m.company_jobs_card_duration_label()}</strong> ${props.job.duration}</p>
          <p><strong>${m.company_jobs_card_slots_label()}</strong> ${props.job.availableSlots}</p>
          <p>
            <strong>${m.company_jobs_card_start_label()}</strong> ${
              props.job.startDate ? format(new Date(props.job.startDate), 'P') : ''
            }
          </p>
          <p><strong>${m.applications()}</strong> ${props.job.numApplicants}</p>
          <div class="card-actions mt-4 flex flex-wrap gap-2">
            <button
              class="btn btn-sm btn-outline"
              @click=${(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                props.onDuplicate(props.job);
              }}
            >
              ${m.company_jobs_duplicate_button()}
            </button>
            <button
              class="btn btn-sm btn-outline"
              @click=${(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                goTo(companyRouter.link('jobDetail') + '/' + props.job._id! + '/edit');
              }}
            >
              ${m.company_jobs_edit_button()}
            </button>
            <button
              class="btn btn-sm btn-outline"
              @click=${(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                goTo(
                  companyRouter.link('jobDetail') +
                    '/' +
                    props.job._id! +
                    '/edit?jumpToMeetings=true&returnTo=' +
                    encodeURIComponent(window.location.pathname),
                );
              }}
            >
              ${m.company_jobs_meetings_button()}
            </button>
            <button
              class="btn btn-sm btn-outline"
              @click=${(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                handleDetails();
              }}
            >
              ${m.applications()}
            </button>
            <button
              class="btn btn-sm btn-outline btn-error"
              @click=${(e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                props.onDelete(props.job);
              }}
            >
              ${m.company_jobs_delete_button()}
            </button>
          </div>
        </div>
      </div>
    `;
  },
);

export const CompanyJobs = stateful(({ companyID }: { companyID: string }) => {
  if (!companyID) return html``;
  const jobs = useQuery(() => {
    // TODO (low prio) not really nice
    return subscribeToJobPositionsByCompany(
      0,
      1000,
      new AdornisFilter({ search: companyID }),
    )(selectionSet(() => JobPosition, 2));
  }, [companyID]);
  if ((jobs.value?.length ?? 0) > 999) return m.company_jobs_too_many_jobs();

  const handleCreateNew = () => {
    goTo(companyRouter.link('jobDetail') + '/new/edit');
  };

  const handleDelete = async (job: JobPosition) => {
    const result = await XNativeDialog.confirm(m.company_jobs_delete_confirm_title(), {
      acceptText: m.company_jobs_delete_confirm_accept(),
      declineText: m.company_jobs_delete_confirm_decline(),
    });
    if (!result) return;
    await job.remove();
  };

  const handleDuplicate = async (job: JobPosition) => {
    // Interview time slots must not be duplicated, otherwise they could be booked twice.
    const { _id, timeSlots, ...jobDataWithoutId } = job.toObject();
    const jobData = { ...jobDataWithoutId, title: `${jobDataWithoutId.title}${m.company_jobs_copy_suffix()}` };
    const newJob = new JobPosition(jobData);
    try {
      const newID = await newJob.create();
      goTo(companyRouter.link('jobDetail') + '/' + newID + '/edit');
    } catch (error) {
      // e.g. the source job is missing required fields like the description.
      void XSnackbar.show(formatFormError(error, 'job_form_'));
    }
  };

  return html`
    <div class="p-4 sm:p-8">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold">${m.company_jobs_title()}</h1>
        <button class="btn btn-primary" @click=${handleCreateNew}>
          <span class="icon-[material-symbols--add]"></span>
          ${m.company_jobs_create_new_button()}
        </button>
      </div>
      ${jobs.loading ? html`<div class="text-center p-8"><span class="loading loading-lg"></span></div>` : ''}
      ${
        !jobs.loading && !jobs.value?.length
          ? html`
              <div class="text-center p-8 bg-base-100 rounded-box">
                <p>${m.company_jobs_no_jobs_message()}</p>
              </div>
            `
          : ''
      }
      <div
        class="${
          jobs.value?.length === 1
            ? 'max-w-md'
            : 'grid auto-rows-fr gap-6 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]'
        }"
      >
        ${repeat(
          jobs.value ?? [],
          job => job._id,
          job => JobCard({ job, onDelete: handleDelete, onDuplicate: handleDuplicate }),
        )}
      </div>
    </div>
  `;
});
